import { Box, Button, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { BetStatus, LastBuyTicketData } from "../../api";
import { BET_OPTIONS } from "../../utils/gameRules";
import { useCustomColors } from "../../hooks/useCustomColors";
import { goldShineSx } from "../../utils/goldShine";

interface LastBuyTicketProps {
  ticket: LastBuyTicketData;
}

const BET_ROW_HEIGHT = 69;
const VISIBLE_ROWS = 3;
const SECONDS_PER_ROW = 2.5;

const MY_BETS_ROUTE = "/history?tab=bets";

const STATUS_KEY: Record<BetStatus, string> = {
  PENDING: "status_pending",
  WON: "status_won",
  LOST: "status_lost",
  VOID: "status_void",
};

const LastBuyTicket = ({ ticket }: LastBuyTicketProps) => {
  const colors = useCustomColors();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const formatMoney = (amount: number) =>
    `${ticket.currency} ${amount.toLocaleString("en-MY", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const statusTint: Record<BetStatus, string> = {
    PENDING: colors.accent.amber,
    WON: colors.accent.green,
    LOST: colors.text.disabled,
    VOID: colors.accent.cyan,
  };

  const eyebrowSx = {
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "3px",
    color: colors.text.secondary,
  };

  const isMarquee = ticket.bets.length > VISIBLE_ROWS;
  // Duplicated list + translateY(-50%) makes the loop seamless.
  const rows = isMarquee ? [...ticket.bets, ...ticket.bets] : ticket.bets;

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: "24px",
        p: { xs: "24px 20px", md: "28px" },
        border: `1px solid ${colors.border.primary}`,
        background: `linear-gradient(145deg, ${colors.card.background} 0%, ${colors.tint.primaryStrong} 100%)`,
        boxShadow: colors.card.shadow,
      }}
    >
      <Stack direction="row" justifyContent="space-between" sx={{ gap: "12px" }}>
        <Box>
          <Typography sx={eyebrowSx}>{t("next_draw")}</Typography>
          <Typography
            sx={{
              mt: "6px",
              fontSize: "20px",
              fontWeight: 800,
              color: colors.text.primary,
            }}
          >
            {t("last_buy_ticket")}
          </Typography>
          <Typography
            sx={{ mt: "4px", fontSize: "12.5px", color: colors.text.secondary }}
          >
            {t("lotto_game")} {ticket.game_number}
          </Typography>
        </Box>
        <Box
          sx={{
            height: "24px",
            px: "12px",
            display: "inline-flex",
            alignItems: "center",
            alignSelf: "flex-start",
            borderRadius: "12px",
            fontSize: "11px",
            fontWeight: 700,
            whiteSpace: "nowrap",
            color: statusTint[ticket.status],
            backgroundColor: `${statusTint[ticket.status]}29`,
            border: `1px solid ${statusTint[ticket.status]}3D`,
          }}
        >
          {ticket.status_text ?? t(STATUS_KEY[ticket.status])}
        </Box>
      </Stack>

      <Box
        sx={{
          mt: "20px",
          flex: "0 0 auto",
          minHeight: 0,
          maxHeight: `${VISIBLE_ROWS * BET_ROW_HEIGHT}px`,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            "@keyframes lastBuyTicketMarquee": {
              to: { transform: "translateY(-50%)" },
            },
            animation: isMarquee
              ? `lastBuyTicketMarquee ${
                  ticket.bets.length * SECONDS_PER_ROW
                }s linear infinite`
              : "none",
          }}
        >
        {rows.map((bet, index) => (
          <Box
            key={`${index}-${bet.position}-${bet.option}`}
            sx={{
              display: "grid",
              gridTemplateColumns: "36px minmax(0, 1fr) auto",
              alignItems: "center",
              columnGap: "10px",
              py: "14px",
              "&:not(:last-of-type)": {
                borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
              },
            }}
          >
            {/* List index, not the betting position. */}
            <Box
              sx={{
                width: 28,
                height: 28,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "9px",
                fontSize: "13px",
                fontWeight: 700,
                color: colors.accent.goldSoft,
                backgroundColor: colors.tint.gold,
                border: `1px solid ${colors.tint.primaryStrong}`,
              }}
            >
              {(index % ticket.bets.length) + 1}
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: colors.text.primary,
                }}
              >
                {t("game_bet_position_value", { position: bet.position })}
              </Typography>
              <Typography
                sx={{
                  mt: "2px",
                  fontSize: "12px",
                  fontWeight: 700,
                  color: colors.accent.cyan,
                }}
              >
                {t(
                  BET_OPTIONS.find((item) => item.option === bet.option)
                    ?.labelKey ?? "common_status"
                )}
              </Typography>
            </Box>

            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 700,
                color: colors.text.primary,
                whiteSpace: "nowrap",
              }}
            >
              {formatMoney(bet.amount)}
            </Typography>
          </Box>
        ))}
        </Box>
      </Box>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          mt: "4px",
          pt: "18px",
          gap: "12px",
          borderTop: `1px solid ${colors.border.accent}`,
        }}
      >
        <Typography sx={{ ...eyebrowSx, letterSpacing: "2px" }}>
          {t("game_bet_total_stake")}
        </Typography>
        <Typography
          sx={{ fontSize: "20px", fontWeight: 900, ...goldShineSx(colors) }}
        >
          {formatMoney(ticket.total_stake)}
        </Typography>
      </Stack>

      <Button
        onClick={() => navigate(MY_BETS_ROUTE)}
        variant="outlinedDark"
        sx={{ mt: { xs: "26px", lg: "auto" }, height: "44px", borderRadius: "12px", flexShrink: 0 }}
      >
        {t("view_my_bets")}
      </Button>
    </Box>
  );
};

export default LastBuyTicket;
